import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, FileText, Paperclip, Plus, Clock, DollarSign, Eye, RotateCcw, Check, MoreVertical, Trash2, X, ArrowDownToLine, Pencil } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { INVOICES_DATA, INVOICES_CHAT_RESPONSES } from '../../data/invoicesData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const STAGE_COLORS = {
  Draft: { bg: 'bg-slate-500/10', text: 'text-slate-500', border: 'border-slate-500/20' },
  Sent: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
  Viewed: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/20' },
  Paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  Overdue: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};
const LIGHT_STAGE_COLORS = {
  Draft: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  Sent: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  Viewed: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
  Paid: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  Overdue: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

const InvoiceCard = ({ invoice, isDark, onAction, onMenu }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const isOverdue = invoice.stage === 'Overdue';
  const isPaid = invoice.stage === 'Paid';
  const isDraft = invoice.stage === 'Draft';
  const cardBg = isOverdue ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : isPaid ? (isDark ? 'bg-emerald-950/10' : 'bg-emerald-50/30') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = isOverdue ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : isPaid ? (isDark ? 'border-emerald-500/10' : 'border-emerald-200/40') : border;

  let ctas = [];
  if (isDraft) ctas = [{ label: 'Send', icon: Send }];
  else if (invoice.stage === 'Sent') ctas = [{ label: 'Resend', icon: RotateCcw }];
  else if (invoice.stage === 'Viewed') ctas = [{ label: 'Follow Up', icon: Send }];
  else if (isOverdue) ctas = [{ label: 'Send Reminder', icon: AlertTriangle }, { label: 'Escalate', icon: AlertTriangle, variant: 'amber' }];
  else if (isPaid) ctas = [{ label: 'View Receipt', icon: Eye }];

  const menuItems = [
    { label: 'View Invoice', icon: Eye },
    { label: 'Edit Invoice', icon: Pencil },
    { label: 'Download PDF', icon: ArrowDownToLine },
    { label: 'Cancel Invoice', icon: X, danger: false },
    { label: 'Delete', icon: Trash2, danger: true },
  ];

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-2.5 mb-2 relative`} data-testid={`invoice-card-${invoice.id}`}>
      <div className="flex items-center justify-between mb-1">
        <p className={`text-[11px] font-semibold ${tp}`}>{invoice.client}</p>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${isOverdue ? (isDark ? 'text-red-400' : 'text-red-500') : isPaid ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : tp}`}>
            ${(invoice.amount / 1000).toFixed(0)}K
          </span>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`p-0.5 rounded ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`} data-testid={`invoice-kebab-${invoice.id}`}>
            <MoreVertical className={`w-3.5 h-3.5 ${ts}`} />
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className={`absolute right-2 top-8 z-20 w-36 rounded-lg border shadow-lg py-1 ${isDark ? 'bg-[#0f1d32] border-white/[0.08]' : 'bg-white border-black/[0.08]'}`} data-testid={`invoice-menu-${invoice.id}`}>
          {menuItems.map(mi => {
            const MIcon = mi.icon;
            return (
              <button key={mi.label} onClick={() => { setMenuOpen(false); onMenu?.(invoice, mi.label); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-[10px] font-medium transition-colors ${mi.danger ? (isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50') : (isDark ? `${tp} hover:bg-white/[0.04]` : `${tp} hover:bg-black/[0.03]`)}`}
                data-testid={`invoice-menu-item-${mi.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <MIcon className="w-3 h-3" />{mi.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2 mb-1">
        <FileText className={`w-3 h-3 ${ts}`} />
        <span className={`text-[9px] ${ts}`}>{invoice.number}</span>
      </div>
      <p className={`text-[9px] ${ts} truncate`}>{invoice.items}</p>
      <div className="flex items-center gap-3 mt-1.5">
        {isOverdue && (
          <span className={`text-[9px] font-medium flex items-center gap-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
            <Clock className="w-3 h-3" /> {invoice.daysOutstanding}d overdue
          </span>
        )}
        {!isOverdue && !isPaid && (
          <span className={`text-[9px] ${ts}`}>Due: {invoice.dueDate}</span>
        )}
        {isPaid && <span className={`text-[9px] flex items-center gap-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}><Check className="w-3 h-3" /> Paid</span>}
        <span className={`text-[8px] ${ts}`}>{invoice.contact}</span>
      </div>
      {ctas.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          {ctas.map(cta => {
            const Icon = cta.icon;
            const isAmber = cta.variant === 'amber';
            return (
              <button key={cta.label} onClick={() => onAction?.(invoice, cta.label)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${
                  isAmber ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' :
                  isPaid ? `${isDark ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]' : 'bg-black/[0.03] text-slate-500 hover:bg-black/[0.06]'}` :
                  `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`
                }`}
                data-testid={`invoice-cta-${invoice.id}-${cta.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-3 h-3" />{cta.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Finn · {item.time}</p>
      </div>
    </div>
  );
};

const V4FinanceInvoices = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [stageFilter, setStageFilter] = useState(null);
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const stages = INVOICES_DATA.stages;
  const filteredInvoices = stageFilter ? INVOICES_DATA.invoices.filter(inv => inv.stage === stageFilter) : INVOICES_DATA.invoices;

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || INVOICES_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleAction = (invoice, action) => {
    sendMessage(`${action} for invoice ${invoice.number} (${invoice.client}).`, { text: `I'll ${action.toLowerCase()} for ${invoice.client}'s invoice ${invoice.number} ($${(invoice.amount / 1000).toFixed(0)}K).\n\nProcessing now — I'll confirm once it's done. Anything else?` });
  };
  const handleMenu = (invoice, action) => {
    sendMessage(`${action} — ${invoice.number} (${invoice.client}).`, { text: `I'll ${action.toLowerCase()} for invoice ${invoice.number} (${invoice.client}). Processing now.\n\nAnything else you need?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Finn about invoices..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="invoices-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.finn} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Finn</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="invoices-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="invoices-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="invoices-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Invoices</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.finn} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  const invoicesByStage = {};
  stages.forEach(s => { invoicesByStage[s] = filteredInvoices.filter(inv => inv.stage === s); });

  return (
    <div className="flex flex-col h-full" data-testid="invoices-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <FileText className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Invoices</p>
            <span className={`text-[10px] ${ts}`}>{filteredInvoices.length} total</span>
          </div>
          <button onClick={() => sendMessage('Create a new invoice.', { text: "I'll draft a new invoice for you. Which client is this for, and what's the amount and line items? I'll populate everything else automatically." })} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="invoices-create-btn">
            <Plus className="w-3.5 h-3.5" /> Create Invoice
          </button>
        </div>
        <div className="flex items-center gap-1">
          {[null, ...stages].map(s => (
            <button key={s || 'all'} onClick={() => setStageFilter(s)}
              className={`px-2 py-1 rounded-lg text-[10px] font-medium border transition-colors ${stageFilter === s ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
              data-testid={`invoices-filter-${(s || 'all').toLowerCase()}`}
            >{s || 'All'}</button>
          ))}
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
        <div className="grid grid-cols-6 gap-3">
          {INVOICES_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Kanban */}
        <div className="flex gap-3 min-h-0 overflow-x-auto">
          {stages.map(stage => {
            const stageInvoices = invoicesByStage[stage] || [];
            const sc = isDark ? STAGE_COLORS[stage] : LIGHT_STAGE_COLORS[stage];
            const total = stageInvoices.reduce((sum, inv) => sum + inv.amount, 0);
            return (
              <div key={stage} className="w-[220px] flex-shrink-0 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sc?.bg} ${sc?.text} ${sc?.border}`}>{stage}</span>
                    <span className={`text-[10px] ${ts}`}>{stageInvoices.length}</span>
                  </div>
                  {total > 0 && <span className={`text-[10px] font-medium ${tp}`}>${(total / 1000).toFixed(0)}K</span>}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {stageInvoices.map(inv => <InvoiceCard key={inv.id} invoice={inv} isDark={isDark} onAction={handleAction} onMenu={handleMenu} />)}
                  {stageInvoices.length === 0 && <p className={`text-[10px] ${ts} text-center py-4`}>No invoices</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.finn} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Finn's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {INVOICES_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4FinanceInvoices;
